import React from 'react';

import {
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	FocusIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TrelloIcon,
} from '@atlaskit/logo';

import GlobeIcon from '@atlaskit/icon/core/globe';

import AdobexdIcon from './product-icons/adobe-xd';
import AdobesignIcon from './product-icons/adobe-sign';
import AhaIcon from './product-icons/aha';
import AirtableIcon from './product-icons/airtable';
import AmplitudeIcon from './product-icons/amplitude';
import AsanaIcon from './product-icons/asana';
import AzuredevopsIcon from './product-icons/azure-devops';
import BoxIcon from './product-icons/box';
import CanvaIcon from './product-icons/canva';
import DocusignIcon from './product-icons/docusign';
import DovetailIcon from './product-icons/dovetail';
import DropboxIcon from './product-icons/dropbox';
import FigmaIcon from './product-icons/figma';
import FreshserviceIcon from './product-icons/freshservice';
import GmailIcon from './product-icons/gmail';
import GithubIcon from './product-icons/github';
import GitlabIcon from './product-icons/gitlab';
import GooglecalendarIcon from './product-icons/google-calendar';
import HubspotIcon from './product-icons/hubspot';
import LucidIcon from './product-icons/lucid';
import LoomIcon from './product-icons/loom';
import MiroIcon from './product-icons/miro';
import MondayIcon from './product-icons/monday';
import MuralIcon from './product-icons/mural';
import NotionIcon from './product-icons/notion';
import OutlookcalendarIcon from './product-icons/outlook-calendar';
import OutlookmailIcon from './product-icons/outlook-mail';
import PipedriveIcon from './product-icons/pipedrive';
import SalesforceIcon from './product-icons/salesforce';
import SentryIcon from './product-icons/sentry';
import SharepointIcon from './product-icons/sharepoint';
import SlackIcon from './product-icons/slack';
import SmartsheetIcon from './product-icons/smartsheet';
import StripeIcon from './product-icons/stripe';
import TeamsIcon from './product-icons/teams';
import TodoistIcon from './product-icons/todoist';
import WebexIcon from './product-icons/webex';
import ZeplinIcon from './product-icons/zeplin';
import ZendeskIcon from './product-icons/zendesk';
import GitbookIcon from './product-icons/gitbook';
import LaunchDarklyIcon from './product-icons/launchdarkly';
import PagerDutyIcon from './product-icons/pagerduty';
import PowerBIIcon from './product-icons/power-bi';
import ServiceNowIcon from './product-icons/servicenow';
import AWSS3Icon from './product-icons/aws-s3';
import GoogleDriveIcon from './product-icons/google-drive';
import { RovoLogoFromLogo, get3PLogo, RovoIconFromIcon } from './icon';

const ProductKeys3P = {
	AtlassianWebCrawler: 'atlassian-web-crawler',
	Figma: 'figma',
	Github: 'github',
	GoogleDrive: 'drive',
	Loom: 'loom',
	OneDrive: 'onedrive',
	Dropbox: 'dropbox',
	Slack: 'slack',
	Sharepoint: 'sharepoint',
	Teams: 'teams',
	GoogleCalendar: 'google-calendar',
	OutlookCalendar: 'outlook-calendar',
	OutlookMail: 'outlook-mail',
	Gmail: 'gmail',
	AdobeSign: 'adobe-sign',
	Asana: 'asana',
	Zendesk: 'zendesk',
	Airtable: 'airtable',
	Smartsheet: 'smartsheet',
	Notion: 'notion',
	Miro: 'miro',
	Docusign: 'docusign',
	Lucid: 'lucid',
	Monday: 'monday',
	AzureDevops: 'azure-devops',
	Gitlab: 'gitlab',
	ConfluenceDC: 'confluence-dc',
	Canva: 'canva',
	Box: 'box',
	Zeplin: 'zeplin',
	ServiceNow: 'servicenow',
	Freshservice: 'freshservice',
	PowerBI: 'power-bi',
	ClickUp: 'clickup',
	Amplitude: 'amplitude',
	Stripe: 'stripe',
	AdobeXD: 'adobexd',
	Aha: 'aha',
	Mural: 'mural',
	Webex: 'webex',
	Todoist: 'todoist',
	PagerDuty: 'pagerduty',
	Pipedrive: 'pipedrive',
	Hubspot: 'hubspot',
	Dovetail: 'dovetail',
	LaunchDarkly: 'launchdarkly',
	Salesforce: 'salesforce',
	Sentry: 'sentry',
	JiraDC: 'jira-dc',
	JsmDC: 'jsm-dc',
	Gitbook: 'gitbook',
	AWSS3: 'aws-s3',
} as const;

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Trello: 'trello',
	Focus: 'mercury',
};

const products1PIcons = {
	[ProductKeys1P.Confluence]: RovoLogoFromLogo(RovoIconFromIcon(ConfluenceIcon), 'Confluence'),
	[ProductKeys1P.Atlas]: RovoLogoFromLogo(AtlassianIcon, 'Home'),
	[ProductKeys1P.Jira]: RovoLogoFromLogo(RovoIconFromIcon(JiraIcon), 'Jira'),
	[ProductKeys1P.JiraProductDiscovery]: RovoLogoFromLogo(
		RovoIconFromIcon(JiraProductDiscoveryIcon),
		'Jira Product Discovery',
	),
	[ProductKeys1P.JiraServiceManagement]: RovoLogoFromLogo(
		RovoIconFromIcon(JiraServiceManagementIcon),
		'Jira Service Management',
	),
	[ProductKeys1P.Bitbucket]: RovoLogoFromLogo(RovoIconFromIcon(BitbucketIcon), 'Bitbucket'),
	[ProductKeys1P.Trello]: RovoLogoFromLogo(RovoIconFromIcon(TrelloIcon), 'Trello'),
	[ProductKeys1P.Compass]: RovoLogoFromLogo(RovoIconFromIcon(CompassIcon), 'Compass'),
	[ProductKeys1P.Focus]: RovoLogoFromLogo(RovoIconFromIcon(FocusIcon), 'Focus'),
};

const products3PIcons = {
	[ProductKeys3P.AdobeXD]: get3PLogo(AdobexdIcon, 'Adobe XD'),
	[ProductKeys3P.AdobeSign]: get3PLogo(AdobesignIcon, 'Adobe Sign'),
	[ProductKeys3P.Aha]: get3PLogo(AhaIcon, 'Aha'),
	[ProductKeys3P.Airtable]: get3PLogo(AirtableIcon, 'Airtable'),
	[ProductKeys3P.Amplitude]: get3PLogo(AmplitudeIcon, 'Amplitude'),
	[ProductKeys3P.Asana]: get3PLogo(AsanaIcon, 'Asana'),
	[ProductKeys3P.AzureDevops]: get3PLogo(AzuredevopsIcon, 'Azure Devops'),
	[ProductKeys3P.Box]: get3PLogo(BoxIcon, 'Box'),
	[ProductKeys3P.Canva]: get3PLogo(CanvaIcon, 'Canva'),
	[ProductKeys3P.Docusign]: get3PLogo(DocusignIcon, 'Docusign'),
	[ProductKeys3P.Dovetail]: get3PLogo(DovetailIcon, 'Dovetail'),
	[ProductKeys3P.Dropbox]: get3PLogo(DropboxIcon, 'Dropbox'),
	[ProductKeys3P.Figma]: get3PLogo(FigmaIcon, 'Figma'),
	[ProductKeys3P.Freshservice]: get3PLogo(FreshserviceIcon, 'Freshservice'),
	[ProductKeys3P.Gmail]: get3PLogo(GmailIcon, 'Gmail'),
	[ProductKeys3P.Github]: get3PLogo(GithubIcon, 'Github'),
	[ProductKeys3P.Gitlab]: get3PLogo(GitlabIcon, 'Gitlab'),
	[ProductKeys3P.GoogleCalendar]: get3PLogo(GooglecalendarIcon, 'Google Calendar'),
	[ProductKeys3P.Hubspot]: get3PLogo(HubspotIcon, 'Hubspot'),
	[ProductKeys3P.Lucid]: get3PLogo(LucidIcon, 'Lucid'),
	[ProductKeys3P.Loom]: get3PLogo(LoomIcon, 'Loom'),
	[ProductKeys3P.Miro]: get3PLogo(MiroIcon, 'Miro'),
	[ProductKeys3P.Monday]: get3PLogo(MondayIcon, 'Monday'),
	[ProductKeys3P.Mural]: get3PLogo(MuralIcon, 'Mural'),
	[ProductKeys3P.Notion]: get3PLogo(NotionIcon, 'Notion'),
	[ProductKeys3P.OutlookCalendar]: get3PLogo(OutlookcalendarIcon, 'Outlook Calendar'),
	[ProductKeys3P.OutlookMail]: get3PLogo(OutlookmailIcon, 'Outlook Mail'),
	[ProductKeys3P.Pipedrive]: get3PLogo(PipedriveIcon, 'Pipedrive'),
	[ProductKeys3P.Salesforce]: get3PLogo(SalesforceIcon, 'Salesforce'),
	[ProductKeys3P.Sentry]: get3PLogo(SentryIcon, 'Sentry'),
	[ProductKeys3P.Sharepoint]: get3PLogo(SharepointIcon, 'Sharepoint'),
	[ProductKeys3P.Slack]: get3PLogo(SlackIcon, 'Slack'),
	[ProductKeys3P.Smartsheet]: get3PLogo(SmartsheetIcon, 'Smartsheet'),
	[ProductKeys3P.Stripe]: get3PLogo(StripeIcon, 'Stripe'),
	[ProductKeys3P.Teams]: get3PLogo(TeamsIcon, 'Teams'),
	[ProductKeys3P.Todoist]: get3PLogo(TodoistIcon, 'Todoist'),
	[ProductKeys3P.Webex]: get3PLogo(WebexIcon, 'Webex'),
	[ProductKeys3P.Zeplin]: get3PLogo(ZeplinIcon, 'Zeplin'),
	[ProductKeys3P.Zendesk]: get3PLogo(ZendeskIcon, 'Zendesk'),
	[ProductKeys3P.Gitbook]: get3PLogo(GitbookIcon, 'Gitbook'),
	[ProductKeys3P.AtlassianWebCrawler]: RovoLogoFromLogo(GlobeIcon, 'Atlassian Web Crawler'),
	[ProductKeys3P.ConfluenceDC]: RovoLogoFromLogo(RovoIconFromIcon(ConfluenceIcon), 'Confluence'),
	[ProductKeys3P.JiraDC]: RovoLogoFromLogo(RovoIconFromIcon(JiraIcon), 'Jira'),
	[ProductKeys3P.JsmDC]: RovoLogoFromLogo(
		RovoIconFromIcon(JiraServiceManagementIcon),
		'Jira Service Management',
	),
	[ProductKeys3P.LaunchDarkly]: get3PLogo(LaunchDarklyIcon, 'LaunchDarkly'),
	[ProductKeys3P.PagerDuty]: get3PLogo(PagerDutyIcon, 'PagerDuty'),
	[ProductKeys3P.PowerBI]: get3PLogo(PowerBIIcon, 'PowerBI'),
	[ProductKeys3P.ServiceNow]: get3PLogo(ServiceNowIcon, 'ServiceNow'),
	[ProductKeys3P.AWSS3]: get3PLogo(AWSS3Icon, 'AWSS3'),
	[ProductKeys3P.GoogleDrive]: get3PLogo(GoogleDriveIcon, 'GoogleDrive'),
};

const productIcons = {
	...products1PIcons,
	...products3PIcons,
};

export const Products = {
	...ProductKeys1P,
	...ProductKeys3P,
};

export type ProductKeys = (typeof Products)[keyof typeof Products];

export const ProductIcon = ({ productKey }: { productKey: ProductKeys }) => {
	const Icon = productIcons[productKey as keyof typeof productIcons];
	return Icon ? <Icon label={productKey} /> : null;
};
